<!-- src/views/treatment-plans/TreatmentPlanList.vue -->
<template>
  <div class="treatment-plan-list">
    <!-- Page header with title and action buttons -->
    <div class="d-flex justify-content-between align-items-center mb-4">
      <h1>Planes de Tratamiento</h1>
      <div class="d-flex">
        <button class="btn btn-outline-primary me-2" @click="refreshData">
          <i class="bi bi-arrow-clockwise me-1"></i> Actualizar
        </button>
        <router-link to="/treatment-plans/create" class="btn btn-primary">
          <i class="bi bi-plus-circle me-1"></i> Nuevo Plan
        </router-link>
      </div>
    </div>

    <!-- Loading state -->
    <div v-if="loading" class="d-flex justify-content-center my-5">
      <div class="spinner-border text-primary" role="status">
        <span class="visually-hidden">Cargando...</span>
      </div>
    </div>

    <!-- Error state -->
    <div v-else-if="error" class="alert alert-danger">
      <i class="bi bi-exclamation-triangle-fill me-2"></i>
      {{ error }}
    </div>

    <!-- Search and filter section -->
    <div v-else class="card mb-4">
      <div class="card-body">
        <div class="row g-3">
          <div class="col-md-6">
            <label for="searchQuery" class="form-label">Buscar</label>
            <div class="input-group">
              <span class="input-group-text"><i class="bi bi-search"></i></span>
              <input
                type="text"
                class="form-control"
                id="searchQuery"
                v-model="searchQuery"
                placeholder="Buscar por paciente..."
              >
            </div>
          </div>
          <div class="col-md-3">
            <label for="sortField" class="form-label">Ordenar por</label>
            <select class="form-select" id="sortField" v-model="sortField">
              <option value="created_at">Fecha de creación</option>
              <option value="patient_name">Nombre de paciente</option>
            </select>
          </div>
          <div class="col-md-3">
            <label for="sortOrder" class="form-label">Orden</label>
            <select class="form-select" id="sortOrder" v-model="sortOrder">
              <option value="asc">Ascendente</option>
              <option value="desc">Descendente</option>
            </select>
          </div>
        </div>
      </div>
    </div>

    <!-- Treatment plan list -->
    <div v-if="!loading && !error">
      <!-- Empty state -->
      <div v-if="filteredPlans.length === 0" class="text-center my-5">
        <i class="bi bi-clipboard2-x text-muted display-1"></i>
        <h3 class="mt-3">No se encontraron planes de tratamiento</h3>
        <p class="text-muted">Crea un nuevo plan de tratamiento para comenzar.</p>
        <router-link to="/treatment-plans/create" class="btn btn-primary mt-2">
          <i class="bi bi-plus-circle me-1"></i> Crear nuevo plan
        </router-link>
      </div>

      <!-- Treatment plan cards -->
      <div v-else class="row g-4">
        <div v-for="plan in filteredPlans" :key="plan.id" class="col-md-6 col-lg-4">
          <div class="card h-100">
            <div class="card-body">
              <h5 class="card-title">
                {{ plan.patient_name }}
              </h5>
              <div class="mb-3">
                <span class="text-muted">Creado el:</span> {{ formatDate(plan.created_at) }}
              </div>
            </div>
            <div class="card-footer bg-white d-flex justify-content-between align-items-center">
              <router-link :to="`/patients/${plan.patient}`" class="btn btn-sm btn-outline-primary">
                <i class="bi bi-person me-1"></i> Ver Paciente
              </router-link>
              <router-link :to="`/treatment-plans/${plan.id}`" class="btn btn-sm btn-primary">
                <i class="bi bi-clipboard2-pulse me-1"></i> Ver Plan
              </router-link>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { treatmentPlanService, patientService } from '@/services/api';

export default {
  name: 'TreatmentPlanList',
  data() {
    return {
      loading: true,
      error: null,
      treatmentPlans: [],
      patients: {},
      searchQuery: '',
      sortField: 'created_at',
      sortOrder: 'desc'
    };
  },
  computed: {
    filteredPlans() {
      let plans = [...this.treatmentPlans];
      
      // Apply search filter
      if (this.searchQuery.trim() !== '') {
        const query = this.searchQuery.toLowerCase();
        plans = plans.filter(plan => {
          const patientName = plan.patient_name ? plan.patient_name.toLowerCase() : '';
          return patientName.includes(query);
        });
      }
      
      // Apply sorting
      plans.sort((a, b) => {
        let fieldA, fieldB;
        
        if (this.sortField === 'patient_name') {
          fieldA = a.patient_name || '';
          fieldB = b.patient_name || '';
        } else {
          fieldA = a[this.sortField] || '';
          fieldB = b[this.sortField] || '';
        }
        
        // Handle different data types
        if (typeof fieldA === 'string') {
          fieldA = fieldA.toLowerCase();
          fieldB = fieldB.toLowerCase();
        }
        
        if (this.sortOrder === 'asc') {
          return fieldA > fieldB ? 1 : -1;
        } else {
          return fieldA < fieldB ? 1 : -1;
        }
      });
      
      return plans;
    }
  },
  async created() {
    await this.refreshData();
  },
  methods: {
    async refreshData() {
      this.loading = true;
      this.error = null;
      
      try {
        // Fetch all treatment plans
        const plansResponse = await treatmentPlanService.getAll();
        let plans = plansResponse.data;
        
        // Fetch all patients to get their names
        const patientsResponse = await patientService.getAll();
        const patients = patientsResponse.data;
        
        // Create a map of patient IDs to names for quick lookup
        const patientMap = {};
        patients.forEach(patient => {
          patientMap[patient.id] = `${patient.first_name} ${patient.last_name}`;
        });
        
        // Add patient names to the treatment plans
        plans = plans.map(plan => ({
          ...plan,
          patient_name: patientMap[plan.patient] || 'Paciente desconocido'
        }));
        
        this.treatmentPlans = plans;
      } catch (error) {
        console.error('Error fetching treatment plans:', error);
        this.error = 'No se pudieron cargar los planes de tratamiento. Por favor, intenta de nuevo.';
      } finally {
        this.loading = false;
      }
    },
    formatDate(dateString) {
      if (!dateString) return '';
      
      const date = new Date(dateString);
      
      // Format date in Spanish format (DD/MM/YYYY)
      return date.toLocaleDateString('es-ES');
    }
  }
};
</script>

<style scoped>
.progress {
  height: 8px;
  border-radius: 4px;
}

.card {
  transition: transform 0.3s;
  box-shadow: 0 0.125rem 0.25rem rgba(0, 0, 0, 0.075);
}

.card:hover {
  transform: translateY(-5px);
  box-shadow: 0 0.5rem 1rem rgba(0, 0, 0, 0.1);
}

.text-truncate {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100%;
}
</style>